// TermsOfUsePage.jsx
import React from "react";
import { motion } from "framer-motion";
import { FileText, Clipboard, User, DollarSign } from "lucide-react";

const headerVariants = {
  hidden: { opacity: 0, y: -40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const sectionVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const TermsOfUsePage = () => {
  return (
    <motion.div
      className="min-h-screen w-full bg-gradient-to-br from-indigo-50 via-purple-50 to-white text-gray-800"
      initial="hidden"
      animate="visible"
    >
      <main className="pt-10 pb-24 px-4 sm:px-6 lg:px-8">
        {/* HERO */}
        <header className="text-center mb-16">
          <motion.div variants={headerVariants} className="max-w-4xl mx-auto">
            {/* <FileText className="w-16 h-16 text-indigo-600 mx-auto mb-4" /> */}
            <h1 className="text-5xl md:text-6xl font-extrabold leading-tight pb-1 bg-clip-text text-transparent bg-gradient-to-r from-indigo-700 to-purple-600">
              Terms of Use
            </h1>
            <p className="text-xl text-gray-700 mt-4 max-w-3xl mx-auto">
              These Terms explain how you may use the TalkyToon website and
              learning platform.
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Effective date: {new Date().toLocaleDateString()}
            </p>
          </motion.div>
        </header>

        {/* CONTENT */}
        <motion.section
          variants={sectionVariants}
          className="max-w-5xl mx-auto bg-white rounded-3xl shadow-2xl p-6 md:p-12 border"
        >
          <div className="prose prose-lg max-w-none">
            <h2 className="flex items-center gap-3 text-indigo-700">
              <Clipboard /> Acceptance of Terms
            </h2>
            <p>
              By accessing or using TalkyToon, you agree to be bound by these
              Terms of Use. If you do not agree with any part of these Terms,
              please do not use the platform.
            </p>

            <h2 className="flex items-center gap-3 text-indigo-700 mt-10">
              <User /> Accounts & Parental Responsibility
            </h2>
            <p>
              TalkyToon is designed for children and must be used under parental
              or guardian supervision.
            </p>
            <ul>
              <li>Parents are responsible for their child’s activity</li>
              <li>Account information must be accurate and up to date</li>
              <li>You are responsible for keeping login credentials secure</li>
            </ul>

            <h2 className="flex items-center gap-3 text-indigo-700 mt-10">
              <Clipboard /> Acceptable Use
            </h2>
            <p>You agree not to:</p>
            <ul>
              <li>Use the platform for unlawful purposes</li>
              <li>Attempt to access restricted areas or data</li>
              <li>Disrupt or damage the platform or its users</li>
              <li>Upload harmful, abusive, or inappropriate content</li>
            </ul>

            <h2 className="flex items-center gap-3 text-indigo-700 mt-10">
              <DollarSign /> Subscriptions & Payments
            </h2>
            <p>
              Some features of TalkyToon may require payment. Any pricing,
              billing cycles, or free trials will be clearly communicated at
              the time of purchase.
            </p>
            <ul>
              <li>Payments are processed securely by third-party providers</li>
              <li>We do not store card or payment details</li>
              <li>Subscriptions may be changed or discontinued with notice</li>
            </ul>

            <h2 className="flex items-center gap-3 text-indigo-700 mt-10">
              <FileText /> Intellectual Property
            </h2>
            <p>
              All content, features, designs, and materials on TalkyToon are the
              property of TalkyToon and may not be copied, reproduced, or
              distributed without permission.
            </p>

            <h2 className="flex items-center gap-3 text-indigo-700 mt-10">
              <Clipboard /> Termination
            </h2>
            <p>
              We reserve the right to suspend or terminate access to the platform
              if these Terms are violated or if misuse is detected.
            </p>

            <h2 className="flex items-center gap-3 text-indigo-700 mt-10">
              <FileText /> Limitation of Liability
            </h2>
            <p>
              TalkyToon is provided on an “as is” basis. We are not liable for
              indirect damages or losses resulting from the use of the platform.
            </p>

            <h2 className="flex items-center gap-3 text-indigo-700 mt-10">
              <FileText /> Contact Us
            </h2>
            <p>
              If you have any questions about these Terms, please contact us:
            </p>
            <div className="mt-4 p-4 bg-gray-50 rounded-xl border">
              <strong>Email:</strong>{" "}
              <a
                href="mailto:talkytoonofficial@gmail.com"
                className="text-indigo-600 font-semibold"
              >
                talkytoonofficial@gmail.com
              </a>
            </div>
          </div>
        </motion.section>
      </main>
    </motion.div>
  );
};

export default TermsOfUsePage;
